import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import {
  contacts,
  profile,
  publicSignals,
  selectedExperience,
  stack,
  workingRange,
} from "../content/portfolio";
import "../styles/terminal.css";
import CurlCommand from "./curlCommand";

const COMMAND_NAMES = [
  "help",
  "profile",
  "experience",
  "public",
  "contact",
  "clear",
];

const staticCommands = {
  profile: (
    <div className="terminal-output-block">
      <p>{profile.headline}</p>
      <p>{profile.supporting}</p>
      <p>
        Range: {workingRange.join(" · ")}
        <br />
        Stack: {stack.join(" · ")}
      </p>
    </div>
  ),
  experience: (
    <div className="terminal-output-block">
      {selectedExperience.map((experience) => (
        <div className="terminal-case-file" key={experience.index}>
          <p>CASE {experience.index}</p>
          <p>{experience.title}</p>
          <p>{experience.role.join(" · ")}</p>
          <p>{experience.summary}</p>
          <p>{experience.metrics.join(" · ")}</p>
          <p>Status: {experience.status}</p>
        </div>
      ))}
    </div>
  ),
  public: (
    <div className="terminal-output-block terminal-output-links">
      <p>Approved public signals:</p>
      {publicSignals.map((signal) => (
        <a
          key={signal.href}
          href={signal.href}
          rel="noreferrer"
          target="_blank"
          aria-label={signal.label}
        >
          <span>{signal.kind}</span>
          {signal.label}
        </a>
      ))}
    </div>
  ),
  contact: (
    <div className="terminal-output-block terminal-output-links">
      <p>Direct channels:</p>
      {contacts.map((contact) => (
        <a
          key={contact.label}
          href={contact.href}
          rel={contact.external ? "noreferrer" : undefined}
          target={contact.external ? "_blank" : undefined}
          aria-label={contact.label}
        >
          <span>{contact.label}</span>
          {contact.value}
        </a>
      ))}
    </div>
  ),
  clear: "clear",
};

export function TerminalContent({ onClose }) {
  const [history, setHistory] = useState([
    { prompt: "➜", directory: "~", isWelcome: true },
  ]);
  const [input, setInput] = useState("");
  const [commandIndex, setCommandIndex] = useState(-1);
  const [commandHistory, setCommandHistory] = useState([]);

  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  const commandsRef = useRef(null);
  const handleCommandRef = useRef(null);
  const shouldAutoFocusInput = useMemo(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return true;
    }
    return !window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  }, []);

  const commands = useMemo(
    () => ({
      ...staticCommands,
      help: (
        <>
          <p>
            Available commands:{" "}
            {COMMAND_NAMES.map((cmd, i) => (
              <Fragment key={cmd}>
                <button
                  type="button"
                  className="command-link"
                  onClick={() => handleCommandRef.current?.(cmd)}
                >
                  {cmd}
                </button>
                {i < COMMAND_NAMES.length - 1 ? ", " : ". "}
              </Fragment>
            ))}
          </p>
          <p>
            Try your actual terminal: <CurlCommand />
          </p>
        </>
      ),
    }),
    [],
  );

  const handleCommand = useCallback((command) => {
    const cmds = commandsRef.current;
    if (!cmds) return;
    let output;
    if (command === "clear") {
      setHistory((prev) => prev.filter((e) => e.isWelcome));
      setCommandHistory((prev) => [...prev, command]);
      setCommandIndex(-1);
      return;
    }
    if (command.startsWith("echo ")) {
      output = command.slice(5);
    } else if (command.startsWith("cat ")) {
      const fileName = command.slice(4).trim();
      output = cmds[fileName] ?? `cat: ${fileName}: No such file or directory`;
    } else {
      output = cmds[command] ?? "Command not found";
    }
    setHistory((prev) => [
      ...prev,
      { prompt: "➜", directory: "~", command },
      { command: output },
    ]);
    setCommandHistory((prev) => [...prev, command]);
    setCommandIndex(-1);
  }, []);

  commandsRef.current = commands;
  handleCommandRef.current = handleCommand;

  useEffect(() => {
    if (shouldAutoFocusInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [shouldAutoFocusInput]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      handleCommand(input.trim());
      setInput("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (
        commandHistory.length > 0 &&
        commandIndex < commandHistory.length - 1
      ) {
        const newIndex = commandIndex + 1;
        setCommandIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (commandIndex > 0) {
        const newIndex = commandIndex - 1;
        setCommandIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else if (commandIndex === 0) {
        setCommandIndex(-1);
        setInput("");
      }
    }
  };

  return (
    <div
      className="terminal"
      onClick={() => {
        if (shouldAutoFocusInput) {
          inputRef.current?.focus();
        }
      }}
    >
      <div className="terminal-header">
        <button
          type="button"
          className="terminal-button close terminal-control"
          onClick={(event) => {
            event.stopPropagation();
            onClose?.();
          }}
          aria-label="Close terminal"
        ></button>
        <span className="terminal-button minimize"></span>
        <span className="terminal-button fullscreen"></span>
      </div>
      <div className="terminal-body" ref={terminalRef}>
        {history.map((entry, index) => (
          <div key={index} className="terminal-line">
            {entry.prompt && <span className="prompt">{entry.prompt}</span>}
            {entry.directory && (
              <span className="directory">{entry.directory}</span>
            )}
            {entry.isWelcome ? (
              <span className="command">
                Type{" "}
                <button
                  type="button"
                  className="command-link"
                  onClick={() => handleCommand("help")}
                >
                  help
                </button>{" "}
                to see available commands.
              </span>
            ) : entry.prompt && typeof entry.command === "string" ? (
              <span className="command-entered">{entry.command}</span>
            ) : (
              <span className="command">{entry.command}</span>
            )}
          </div>
        ))}
        <form onSubmit={handleSubmit} className="terminal-input">
          <span className="prompt">➜</span>
          <span className="directory">~</span>
          <input
            className="input"
            aria-label="Terminal command"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            ref={inputRef}
          />
        </form>
      </div>
    </div>
  );
}

export default function Terminal() {
  return (
    <div className="terminal-wrapper">
      <TerminalContent />
    </div>
  );
}
